import { Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { debounce } from 'debounce';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { FaKeyboard } from 'react-icons/fa';
import { OptionsType, OptionTypeBase } from 'react-select/src/types';
import { getStrategyRules } from '../../../../canopy-ui-components/api/strategy';
import CanopyInput from '../../../../canopy-ui-components/components/CanopyInput/CanopyInput';
import CanopySearch from '../../../../canopy-ui-components/components/CanopySelect/CanopySearch';
import { LoadingIndicator } from '../../../../canopy-ui-components/components/CanopySelect/utils';
import NavSearch from '../../../../canopy-ui-components/components/Icons/NavSearch';
import { useCurrentColors } from '../../../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';

function SecurityTags({
    globalProps: { t },
    userId,
    index,
    updateTags,
    file,
    isDisabled,
}: any) {
    const [keyBoardInputMode, setKeyBoardInputMode] = useState(false);
    const [tags, setTags] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const currentColors = useCurrentColors();

    const loadStrategyRules = async (params: any, callback: any) => {
        const response: Record<any, any> = await getStrategyRules({
            ...params,
            limit: 1000,
        });
        let responseData: Record<any, any>[] = [];
        if (!response?.error) {
            const results: any[] =
                Object.values(response)?.[0]?.[params?.types];
            if (Array.isArray(results)) {
                responseData = results?.map((item: Record<string, string>) => {
                    return {
                        ...item,
                        display: item.display || item.security_name,
                        label: item.ticker,
                        value: item.ticker,
                    };
                });
            } else {
                responseData = [];
            }
        }
        callback(responseData);
    };

    const debouncedGetRules = debounce(loadStrategyRules, 300);

    const loadOptions = (
        inputValue: string,
        callback: (options: OptionsType<OptionTypeBase>) => void
    ) => {
        if (!inputValue) {
            return callback([]);
        }
        debouncedGetRules(
            {
                keyword: inputValue,
                types: 'ticker',
                user_ids: userId,
            },
            callback
        );
    };

    const handleValueChange = (value: any) => {
        setTags(value);
        updateTags &&
            updateTags(
                value?.map((tag: any) => tag.value),
                index
            );
    };

    const handleInputValueChange = (e: any) => {
        const inputValue = e?.target?.value;
        setInputValue(inputValue);
        setTags(
            inputValue?.split(',').filter((value: any) => {
                value = value?.trim();
                if (value !== '') {
                    return {
                        label: value,
                        value,
                    };
                }
            })
        );
        updateTags &&
            updateTags(
                inputValue?.split(',')?.filter((item: string) => item.trim()),
                index
            );
    };

    const handleSwitchInput = () => {
        if (!keyBoardInputMode) {
            setInputValue(tags?.map((item: any) => item?.value)?.join(','));
        }
        setKeyBoardInputMode((keyBoardInputMode) => !keyBoardInputMode);
    };

    useEffect(() => {
        const newTags = file?.securityTags?.map((item: string) => {
            return {
                label: item,
                value: item,
            };
        });
        if (!isEqual(newTags, tags)) {
            setTags(newTags);
        }
    }, [file?.securityTags]);

    return (
        <Flex>
            {keyBoardInputMode ? (
                <CanopyInput
                    onChange={handleInputValueChange}
                    isDisabled={isDisabled}
                    value={inputValue}
                    placeholder={`${t('security-tags')} - ${t('ui.csv')}`}
                    opacity={isDisabled ? '0.6 !important' : '1'}
                    width="100%"
                    fontSize="13px"
                    minWidth="100%"
                    height="30px"
                    boxShadow="none"
                    backgroundColor="transparent"
                    color={currentColors.brand['10']}
                    _hover={{
                        color: currentColors.brand['10'],
                        backgroundColor: 'transparent',
                    }}
                    _focus={{
                        color: currentColors.brand['10'],
                        backgroundColor: 'transparent',
                    }}
                    _active={{
                        color: currentColors.brand['10'],
                        backgroundColor: 'transparent',
                    }}></CanopyInput>
            ) : (
                <CanopySearch
                    loadOptions={loadOptions}
                    onChange={handleValueChange}
                    placeholder={t('security-tags')}
                    isDisabled={isDisabled}
                    value={tags}
                    isMulti={true}
                    getOptionLabel={(option: any) => {
                        return `${option.display} \n ${option.ticker}`;
                    }}
                    t={t}
                    components={{
                        LoadingIndicator,
                    }}
                    loadingMessage={() => `${t('loading')}...`}
                    height={file?.securityTags?.length ? 'max-content' : '30px'}
                    maxHeight="66px"
                    minHeight="30px"
                    isMultiLineOption={true}
                />
            )}
            {keyBoardInputMode ? (
                <Tooltip
                    aria-label="serach-input"
                    label={t('ui.mm.tooltip.search-input')}
                    hasArrow>
                    <IconButton
                        background="transparent"
                        cursor="pointer"
                        border="none"
                        width="20px"
                        height="20px"
                        isDisabled={isDisabled}
                        color={currentColors.whiteAlpha['400']}
                        onClick={handleSwitchInput}
                        variant="ghost"
                        _hover={{ backgroundColor: 'transparent' }}
                        _focus={{
                            border: '0px',
                            backgroundColor: 'transparent',
                            outline: 'none',
                        }}
                        _active={{ border: '0px' }}
                        aria-label="serach-input"
                        icon={<NavSearch />}></IconButton>
                </Tooltip>
            ) : (
                <Tooltip
                    aria-label="key-board-input"
                    label={t('ui.mm.tooltip.keyin-input')}
                    hasArrow>
                    <IconButton
                        background="transparent"
                        cursor="pointer"
                        border="none"
                        width="20px"
                        height="20px"
                        isDisabled={isDisabled}
                        color={currentColors.whiteAlpha['400']}
                        onClick={handleSwitchInput}
                        variant="ghost"
                        _hover={{ backgroundColor: 'transparent' }}
                        _focus={{
                            border: '0px',
                            backgroundColor: 'transparent',
                            outline: 'none',
                        }}
                        _active={{ border: '0px' }}
                        aria-label="key-board-input"
                        icon={<FaKeyboard />}></IconButton>
                </Tooltip>
            )}
        </Flex>
    );
}

export default SecurityTags;
